/**
 * SurveyPopupProvider
 *
 * App-wide client provider for active popup surveys.
 *
 * Primary responsibilities:
 * - Fetch popup surveys eligible for the current user and page/event
 * - Record seen, dismissed, and completed states durably
 * - Render the shared survey form inside a modal
 *
 * Dependencies:
 * - Auth context
 * - User surface interaction API
 * - SurveyForm
 */

'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'

import { useAuth } from '@/context/auth'
import type { SurveyRow } from '@/lib/surveys/types'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { SurveyForm } from '@/components/surveys/survey-form'

interface SurveyTriggerEvent extends Event {
  detail?: {
    event?: string
  }
}

// --------------

function isSurveyTriggerEvent(event: Event): event is SurveyTriggerEvent {
  return event.type === 'losi:survey-trigger'
}

// --------------

export function SurveyPopupProvider() {
  const { user } = useAuth()
  const pathname = usePathname()
  const [survey, setSurvey] = useState<SurveyRow | null>(null)
  const [open, setOpen] = useState(false)
  const loadingRef = useRef(false)
  const sessionClosedRef = useRef<Set<string>>(new Set())

  const recordInteraction = useCallback(
    async (nextSurvey: SurveyRow, action: 'seen' | 'dismissed' | 'completed') => {
      try {
        await fetch('/api/user/surface-interactions', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            surfaceKey: `survey:${nextSurvey.id}`,
            surfaceType: 'survey',
            action,
            metadata: {
              surveyId: nextSurvey.id,
              triggerEvent: nextSurvey.trigger_event,
              showPolicy: nextSurvey.show_policy
            }
          })
        })
      } catch {
        // Survey suppression is best-effort; the API can retry on next event.
      }
    },
    []
  )

  const loadSurvey = useCallback(
    async (eventKey: string) => {
      if (!user?.id || loadingRef.current) {
        return
      }

      loadingRef.current = true

      try {
        const params = new URLSearchParams({
          event: eventKey,
          path: pathname || '/'
        })
        const response = await fetch(`/api/surveys/active?${params}`, {
          cache: 'no-store'
        })

        if (!response.ok) {
          return
        }

        const payload = (await response.json()) as { survey?: SurveyRow | null }
        const nextSurvey = payload.survey ?? null

        if (!nextSurvey || sessionClosedRef.current.has(nextSurvey.id)) {
          return
        }

        setSurvey(nextSurvey)
        setOpen(true)
        void recordInteraction(nextSurvey, 'seen')
      } finally {
        loadingRef.current = false
      }
    },
    [pathname, recordInteraction, user?.id]
  )

  const closeSurvey = useCallback(
    (action: 'dismissed' | 'completed') => {
      if (!survey) {
        setOpen(false)
        return
      }

      sessionClosedRef.current.add(survey.id)
      setOpen(false)
      void recordInteraction(survey, action)
    },
    [recordInteraction, survey]
  )

  useEffect(() => {
    void loadSurvey('app_open')
  }, [loadSurvey])

  useEffect(() => {
    const handleTrigger = (event: Event) => {
      if (!isSurveyTriggerEvent(event)) {
        return
      }

      const eventKey = event.detail?.event?.trim()

      if (eventKey) {
        void loadSurvey(eventKey)
      }
    }

    window.addEventListener('losi:survey-trigger', handleTrigger)
    return () => window.removeEventListener('losi:survey-trigger', handleTrigger)
  }, [loadSurvey])

  if (!survey) {
    return null
  }

  const popupDescription =
    survey.popup_body || survey.description || 'Share a quick response so we can improve Losi.'

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && open) {
          closeSurvey('dismissed')
        } else {
          setOpen(nextOpen)
        }
      }}
    >
      <DialogContent className="gap-5 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl">{survey.popup_title || survey.title}</DialogTitle>
          <DialogDescription className="leading-6">{popupDescription}</DialogDescription>
        </DialogHeader>

        <SurveyForm
          survey={survey}
          source="popup"
          className="max-h-[65dvh] overflow-y-auto pr-1"
          onSubmitted={() => closeSurvey('completed')}
        />

        <Button
          type="button"
          variant="ghost"
          className="w-full"
          aria-label="Dismiss survey"
          onClick={() => closeSurvey('dismissed')}
        >
          Skip for now
        </Button>
      </DialogContent>
    </Dialog>
  )
}
